import React, { useState, useEffect } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { db } from "../../config/firebase";
import DashboardLayout from "../layout";
import AddressForm from "./AddressForm";
import EstimateForm from "./EstimateForm";
import AcceptEstimationForm from "./AcceptEstimationForm";
import Header from "../header/Header";
import { useMyContext } from "../../context/MyContext";
import { FormatDate } from "../../lib/dateFormat";
import { FaCheckCircle } from "react-icons/fa";
import { FaRegEye, FaDownload } from "react-icons/fa6";
import { MdOutlineCancel } from "react-icons/md";
import { PiDotsThreeVerticalBold } from "react-icons/pi";
import { Dropdown, Menu, Space, Tooltip, Modal, Spin } from "antd";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { generateIOR } from "../shared/pdf/generateIOR";
import { api, API_BASE_URL } from "../../config/api";
import Search from "../../icons/Search";
import { Pagination } from "antd";
import Loader from "../shared/loader/loader";
import AskForEstimate from "../shared/askForQuestion/EstimateMessageComponent";
import { useQueryClient } from "@tanstack/react-query";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Loader2 } from "lucide-react";

const NewRequest = () => {
  const { user, notifications } = useMyContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const location = useLocation();

  const [showForm, setShowForm] = useState(false);
  const [requests, setRequests] = useState([]);
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState("request");
  const [showEstimateForm, setShowEstimateForm] = useState(false);
  const [estimates, setEstimates] = useState([]);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isNewEstimate, setIsNewEstimate] = useState(false);
  const [pdfLoading, setPdfLoading] = useState({});
  const [isPdfModalVisible, setIsPdfModalVisible] = useState(false);
  const [currentPdfUrl, setCurrentPdfUrl] = useState(null);
  const [viewLoading, setViewLoading] = useState({});
  const [downloadLoading, setDownloadLoading] = useState({});
  const [estimateStatus, setEstimateStatus] = useState({});
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pdfCache, setPdfCache] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [filteredEstimates, setFilteredEstimates] = useState([]);
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [currentEstimate, setCurrentEstimate] = useState(null);
  const [isLoadingRequests, setIsLoadingRequests] = useState(false);
  const [isLoadingEstimates, setIsLoadingEstimates] = useState(false);
  const [filteredAcceptedEstimates, setFilteredAcceptedEstimates] = useState([]);
  const [filteredPendingEstimates, setFilteredPendingEstimates] = useState([]);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentEstimateId, setCurrentEstimateId] = useState(null);
  const [isAcceptConfirmationVisible, setIsAcceptConfirmationVisible] = useState(false);
  const [estimateToAccept, setEstimateToAccept] = useState(null);
  const [loadingStates, setLoadingStates] = useState({});

  const isPermissionDenied = (error) => {
    const code = error?.code || "";
    const msg = String(error?.message || "");
    return (
      code === "permission-denied" ||
      msg.toLowerCase().includes("insufficient permissions")
    );
  };

  const normalizeRequestStatus = (value) => {
    const v = String(value || "").trim().toLowerCase();
    if (!v) return "unknown";
    if (v === "unquoted") return "unquoted";
    if (v === "quoted") return "quoted";
    return v;
  };

  //helper function to manage multiple action loading states for each row item
  const setLoading = (id, action, value) => {
    setLoadingStates((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [action]: value
      }
    }))
  }

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab) {
      setActiveTab(tab);
    }
  }, [location]);

  const fetchEstimates = async () => {
    if (!user?.uid || isLoadingEstimates) return;

    setIsLoadingEstimates(true);
    try {
      const response = await api.get(
        `${API_BASE_URL}/clientestimates/user/${user.uid}`
      );

      const sortedEstimates = response.data.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      setEstimates(sortedEstimates);

      const acceptedEstimates = sortedEstimates.filter(
        (est) => est.estimationAccept === true
      );
      const pendingEstimates = sortedEstimates.filter(
        (est) => est.estimationAccept !== true
      );

      if (searchQuery) {
        const filteredAccepted = acceptedEstimates.filter((estimate) => {
          return (
            (estimate.estimateId !== undefined &&
              String(estimate.estimateId)
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.originCountry &&
              estimate.originCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.destinationCountry &&
              estimate.destinationCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.createdAt &&
              FormatDate(estimate.createdAt)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
          );
        });

        const filteredPending = pendingEstimates.filter((estimate) => {
          return (
            (estimate.estimateId !== undefined &&
              String(estimate.estimateId)
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.originCountry &&
              estimate.originCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.destinationCountry &&
              estimate.destinationCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.createdAt &&
              FormatDate(estimate.createdAt)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
          );
        });

        setFilteredAcceptedEstimates(filteredAccepted);
        setFilteredPendingEstimates(filteredPending);

        if (activeTab === "estimate") {
          setFilteredEstimates(filteredAccepted);
        } else if (activeTab === "proposal") {
          setFilteredEstimates(filteredPending);
        }
      } else {
        setFilteredAcceptedEstimates(acceptedEstimates);
        setFilteredPendingEstimates(pendingEstimates);

        if (activeTab === "estimate") {
          setFilteredEstimates(acceptedEstimates);
        } else if (activeTab === "proposal") {
          setFilteredEstimates(pendingEstimates);
        }
      }
    } catch (error) {
      console.error("Error fetching estimates:", error);
      toast.error("Error fetching estimates");
    } finally {
      setIsLoadingEstimates(false);
    }
  };

  const handleNewRequestForm = (request) => {
    navigate(`/editRequestForm/${request.id}`);
  };

  const clientEstimateSummaryCache = {};
  const fetchClientEstimateSummary = async (estimateId) => {
    if (clientEstimateSummaryCache[estimateId]) {
      return clientEstimateSummaryCache[estimateId];
    }

    try {
      const response = await api.get(
        `${API_BASE_URL}/clientestimates/summary/${estimateId}`
      );
      clientEstimateSummaryCache[estimateId] = response.data.estimateSummary;
      return response.data.estimateSummary;
    } catch (error) {
      console.error("Error fetching client estimate summary:", error);
      toast.error("Error fetching estimate summary");
      return null;
    }
  };

  const handleViewPdf = async (estimate) => {
    setLoading(estimate.estimateId, "view", true);
    try {
      let pdfUrl = pdfCache[estimate.estimateId];

      if (!pdfUrl) {
        const clientEstimateSummary = await fetchClientEstimateSummary(
          estimate.estimateId
        );
        if (!clientEstimateSummary) {
          throw new Error("Failed to fetch estimate summary");
        }

        const dhlEstimation = estimate.dhlEstimation || null;

        if (!clientEstimateSummary.services.length && !dhlEstimation) {
          toast.warn("Estimate data is incomplete. Unable to generate PDF.");
          setViewLoading({ ...viewLoading, [estimate.estimateId]: false });
          return;
        }

        const pdfBlob = await generateIOR(
          estimate,
          clientEstimateSummary,
          dhlEstimation
        );
        pdfUrl = URL.createObjectURL(pdfBlob);
        setPdfCache((prevCache) => ({
          ...prevCache,
          [estimate.estimateId]: pdfUrl,
        }));
      }
      setCurrentPdfUrl(pdfUrl);
      setIsPdfModalVisible(true);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error(`Error generating PDF: ${error.message}`);
    } finally {
      setLoading(estimate.estimateId, "view", false);
    }
  };

  const handleDownloadPdf = async (estimate) => {
    setLoading(estimate.estimateId, "download", true);
    try {
      const clientEstimateSummary = await fetchClientEstimateSummary(
        estimate.estimateId
      );
      if (!clientEstimateSummary) {
        throw new Error("Failed to fetch estimate summary");
      }

      const dhlEstimation = estimate.dhlEstimation || null;

      if (!clientEstimateSummary.services.length && !dhlEstimation) {
        toast.warn("Estimate data is incomplete. Unable to download PDF.");
        return;
      }

      const pdfBlob = await generateIOR(
        estimate,
        clientEstimateSummary,
        dhlEstimation
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = `estimate_${estimate.estimateId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error("Error downloading PDF:", error);
      toast.error(`Error downloading PDF: ${error.message}`);
    } finally {
      setLoading(estimate.estimateId, "download", false);
    }
  };

  const handleAcceptEstimate = (estimate) => {
    setEstimateToAccept(estimate);
    setIsAcceptConfirmationVisible(true);
  };

  const confirmAcceptEstimate = () => {
    setCurrentEstimate(estimateToAccept);
    setShowAcceptForm(true);
    setIsAcceptConfirmationVisible(false);
    setEstimateToAccept(null);
  };

  const cancelAcceptEstimate = () => {
    setIsAcceptConfirmationVisible(false);
    setEstimateToAccept(null);
  };

  const handleAcceptSubmit = async (updatedEstimation) => {
    setLoading(updatedEstimation.estimateId, "accept", true);
    try {
      await api.put(
        `${API_BASE_URL}/clientestimates/id/${updatedEstimation.estimateId}`,
        updatedEstimation
      );

      const updatedEstimates = estimates.map(est =>
        est.estimateId === updatedEstimation.estimateId
          ? { ...est, ...updatedEstimation, estimationAccept: true }
          : est
      );
      setEstimates(updatedEstimates);

      const acceptedEstimates = updatedEstimates.filter(est => est.estimationAccept === true);
      const pendingEstimates = updatedEstimates.filter(est => est.estimationAccept !== true);

      setFilteredAcceptedEstimates(acceptedEstimates);
      setFilteredPendingEstimates(pendingEstimates);

      if (activeTab === "estimate") {
        setFilteredEstimates(acceptedEstimates);
      } else if (activeTab === "proposal") {
        setFilteredEstimates(pendingEstimates);
      }

      toast.success("Estimate accepted successfully");
      setShowAcceptForm(false);
      setCurrentEstimate(null);
    } catch (error) {
      console.error("Error accepting estimate:", error);
      toast.error("Error accepting estimate");
    } finally {
      setLoading(updatedEstimation.estimateId, "accept", false);
    }
  };

  const handleDeclineEstimate = async (estimateId) => {
    setLoading(estimateId, "decline", true);
    try {
      // Use PUT to decline instead of DELETE
      await api.put(`${API_BASE_URL}/clientestimates/decline/${estimateId}`);

      // Update local state: Mark as declined instead of filtering it out
      const updatedEstimates = estimates.map(est =>
        est.estimateId === estimateId ? { ...est, isDeclined: true } : est
      );
      setEstimates(updatedEstimates);

      const acceptedEstimates = updatedEstimates.filter(est => est.estimationAccept === true);
      const pendingEstimates = updatedEstimates.filter(est => est.estimationAccept !== true);

      setFilteredAcceptedEstimates(acceptedEstimates);
      setFilteredPendingEstimates(pendingEstimates);

      // Update current view lists
      if (activeTab === "estimate") {
        setFilteredEstimates(acceptedEstimates);
      } else if (activeTab === "proposal") {
        setFilteredEstimates(pendingEstimates);
      }

      toast.success("Estimate declined successfully");
    } catch (error) {
      console.error("Error declining estimate:", error);
      toast.error("Error declining estimate");
    } finally {
      setLoading(estimateId, "decline", false);
    }
  };

  const handleClosePdfModal = () => {
    setIsPdfModalVisible(false);
    setCurrentPdfUrl(null);
  };

  useEffect(() => {
    return () => {
      Object.values(pdfCache).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
      setPdfCache({});
    };
  }, []);

  useEffect(() => {
    if (activeTab === "request") {
      fetchRequests();
    } else {
      fetchEstimates();
    }
  }, [user, activeTab]);

  const handleNewRequest = () => {
    if (activeTab === "request") {
      setShowForm(true);
      setShowEstimateForm(false);
      navigate(location.pathname, { replace: true, state: { showForm: true } });
    } else {
      navigate("/oneunion-trade-os");
    }
  };

  const handleBack = () => {
    setShowForm(false);
    navigate("/bookshipment");
  };

  const handleBackFromForm = () => {
    setShowEstimateForm(false);
    setSelectedEstimate(null);
    setIsNewEstimate(false);
    navigate("/bookshipment?tab=proposal");
  };

  useEffect(() => {
    if (location.state && location.state.fromForm) {
      setShowForm(false);
      setShowEstimateForm(false);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const fetchRequests = async () => {
    if (!user?.uid) return;

    setIsLoadingRequests(true);
    try {
      let requestsList = [];

      try {
        const requestsCollection = collection(db, "requests");
        const q = query(requestsCollection, where("userId", "==", user.uid));
        const snapshot = await getDocs(q);
        requestsList = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (firestoreError) {
        if (isPermissionDenied(firestoreError)) {
          const proxyRes = await api.get("/firestore/requests");
          requestsList = Array.isArray(proxyRes.data) ? proxyRes.data : [];
        } else {
          throw firestoreError;
        }
      }

      setRequests(
        requestsList.sort(
          (a, b) => new Date(b.creationDate) - new Date(a.creationDate)
        )
      );

      if (searchQuery) {
        const filtered = requestsList.filter((request) => {
          return (
            (request.name &&
              request.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (request.inquireNumber &&
              request.inquireNumber
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (request.creationDate &&
              FormatDate(request.creationDate)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
          );
        });
        setFilteredRequests(filtered);
      } else {
        setFilteredRequests(requestsList);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      toast.error("Error fetching requests");
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleDelete = (request) => {
    setRequestToDelete(request);
    setIsDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (requestToDelete) {
      try {
        await deleteDoc(doc(db, "requests", requestToDelete.id));
        toast.success("Request deleted successfully");
        fetchRequests();
      } catch (error) {
        console.error("Error deleting document: ", error);
        toast.error("Error deleting request");
      }
    }
    setIsDeleteModalVisible(false);
    setRequestToDelete(null);
  };

  const cancelDelete = () => {
    setIsDeleteModalVisible(false);
    setRequestToDelete(null);
  };

  useEffect(() => {
    fetchRequests();
  }, [user]);

  const handleEditEstimate = (estimate) => {
    setSelectedEstimate(estimate);
    setShowEstimateForm(true);
    setIsNewEstimate(false);
    navigate(`/estimate/${estimate.estimateId}`);
  };

  const handleCreateNewEstimate = () => {
    setSelectedEstimate(null);
    setShowEstimateForm(true);
    setIsNewEstimate(true);
    navigate("/estimate/new", { state: { fromNewRequest: true } });
  };

  useEffect(() => {
    if (id === "new") {
      setShowEstimateForm(true);
      setIsNewEstimate(true);
    } else if (id) {
      fetchEstimateById(id);
    }
  }, [id]);

  const fetchEstimateById = async (estimateId) => {
    try {
      const response = await api.get(
        `${API_BASE_URL}/clientestimates/id/${estimateId}`
      );
      setSelectedEstimate(response.data);
      setShowEstimateForm(true);
      setIsNewEstimate(false);
    } catch (error) {
      console.error("Error fetching estimate:", error);
      toast.error("Failed to fetch estimate. Please try again.");
    }
  };

  useEffect(() => {
    if (activeTab === "request" && Array.isArray(requests)) {
      const filtered = requests.filter((request) => {
        return (
          (request.name &&
            request.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (request.inquireNumber &&
            request.inquireNumber
              .toLowerCase()
              .includes(searchQuery.toLowerCase())) ||
          (request.creationDate &&
            FormatDate(request.creationDate)
              .toLowerCase()
              .includes(searchQuery.toLowerCase()))
        );
      });
      setFilteredRequests(filtered);
    } else if (activeTab === "estimate" && Array.isArray(estimates)) {
      const filteredAccepted = estimates
        .filter((est) => est.estimationAccept === true)
        .filter((estimate) => {
          return (
            (estimate.estimateId !== undefined &&
              String(estimate.estimateId)
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.originCountry &&
              estimate.originCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.destinationCountry &&
              estimate.destinationCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.createdAt &&
              FormatDate(estimate.createdAt)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
          );
        });
      setFilteredEstimates(filteredAccepted);
      setFilteredAcceptedEstimates(filteredAccepted);
    } else if (activeTab === "proposal" && Array.isArray(estimates)) {
      const filteredPending = estimates
        .filter((est) => est.estimationAccept !== true)
        .filter((estimate) => {
          return (
            (estimate.estimateId !== undefined &&
              String(estimate.estimateId)
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.originCountry &&
              estimate.originCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.destinationCountry &&
              estimate.destinationCountry
                .toLowerCase()
                .includes(searchQuery.toLowerCase())) ||
            (estimate.createdAt &&
              FormatDate(estimate.createdAt)
                .toLowerCase()
                .includes(searchQuery.toLowerCase()))
          );
        });
      setFilteredEstimates(filteredPending);
      setFilteredPendingEstimates(filteredPending);
    }
  }, [searchQuery, requests, estimates, activeTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "request") {
      fetchRequests();
    } else if (tab === "estimate") {
      fetchEstimates();
      setFilteredEstimates(filteredAcceptedEstimates);
    } else if (tab === "proposal") {
      fetchEstimates();
      setFilteredEstimates(filteredPendingEstimates);
    }
    setCurrentPage(1);
  };

  const handleCommentClick = async (id, estimateId) => {
    setCurrentEstimateId({ id, estimateId });
    setShowCommentModal(true);
    try {
      await api.put(`/notifications/est/${user.uid}`, {
        role: user?.role,
        quoteNumber: estimateId.toString(),
      });
      queryClient.invalidateQueries(["notifications"]);
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const renderTabButtons = () => {
    return (
      <div className="flex">
        <button
          data-tour-id="quote-request-tab"
          onClick={() => handleTabChange("request")}
          className={`py-2 px-4 font-semibold shadow-lg  ${activeTab === "request"
            ? "text-blue-600  border-blue-600 btnBg flex rounded-[5px] shadow-[#6478b0] shadow-lg"
            : "bg-gray-400 text-white flex rounded-[5px]"
            }`}
        >
          Quote Request
        </button>
        <button
          data-tour-id="proposal-tab"
          onClick={() => handleTabChange("proposal")}
          className={`py-2 px-4 font-semibold shadow-lg  ml-4 ${activeTab === "proposal"
            ? "text-blue-600  border-blue-600 btnBg flex rounded-[5px] shadow-[#6478b0] shadow-lg"
            : "bg-gray-400 text-white flex rounded-[5px] ml-4"
            }`}
        >
          TradeOS™ Quotations
        </button>
        <button
          data-tour-id="ior-estimate-tab"
          onClick={() => handleTabChange("estimate")}
          className={`py-2 px-4 font-semibold shadow-lg  ml-4 ${activeTab === "estimate"
            ? "text-blue-600  border-blue-600 btnBg flex rounded-[5px] shadow-[#6478b0] shadow-lg"
            : "bg-gray-400 text-white flex rounded-[5px] ml-4"
            }`}
        >
          Proposals
        </button>
      </div>
    );
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handlePageChange = (page, pageSize) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  };

  const getRoute = (estimate) => {
    return `${estimate.originCountry} - ${estimate.destinationCountry}`;
  };

  return (
    <DashboardLayout>
      {isPdfModalVisible && (
        <Modal
          title="Estimate"
          visible={isPdfModalVisible}
          onCancel={handleClosePdfModal}
          footer={null}
          width="60%"
        >
          <iframe
            src={`${currentPdfUrl}#toolbar=0`}
            title="Estimate PDF"
            style={{ width: "100%", height: "70vh", border: 0 }}
          ></iframe>
        </Modal>
      )}

      <Modal
        title={
          <div className="flex items-center gap-2">
            <InfoCircleOutlined className="text-blue-600 text-xl" />
            <span className="text-lg font-semibold text-blue-600">
              Final Review Confirmation
            </span>
          </div>
        }
        visible={isAcceptConfirmationVisible}
        onOk={confirmAcceptEstimate}
        onCancel={cancelAcceptEstimate}
        okText="Proceed"
        cancelText="Cancel"
        width={500}
        className="rounded-lg"
        bodyStyle={{
          background: "linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)",
          padding: "20px",
          borderRadius: "8px",
        }}
        okButtonProps={{
          className:
            "bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-1",
        }}
        cancelButtonProps={{
          className:
            "bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-md px-4 py-1",
        }}
      >
        <div className="space-y-4">
          <p className="text-gray-700 text-base leading-relaxed">
            <span className="font-medium text-blue-600">Your Request</span> is
            under final review. Thank you for your understanding!
          </p>
          <p className="text-gray-600 text-sm leading-relaxed">
            Please fill out the complete form for final review and submit it.
            Our Admin will review your submission and take one of the following
            actions:
          </p>
          <ul className="list-disc list-inside text-gray-600 text-sm space-y-2">
            <li>
              <span className="font-medium text-green-600">Accept:</span> The
              Quotation will move directly to{" "}
              <span className="italic">"My Shipments"</span>.
            </li>
            <li>
              <span className="font-medium text-yellow-600">Update:</span> The
              Estimate will be revised, and the updated Quotation will return
              to{" "}
              <span className="italic">"New Quotes"</span> for your final
              approval.
            </li>
          </ul>
        </div>
      </Modal>

      {showAcceptForm && (
        <AcceptEstimationForm
          estimation={currentEstimate}
          onClose={() => setShowAcceptForm(false)}
          onSubmit={handleAcceptSubmit}
        />
      )}

      <div className="w-full h-full">
        {!showForm && !showEstimateForm && <Header title="New Requests" />}
        <div className="p-4 h-full bg-cus-gray overflow-auto">
          <div>
            {!showForm && !showEstimateForm && (
              <div className="bg-white p-4 pb-0 rounded-md">
                <div className="flex flex-wrap items-center justify-between mb-4">
                  {renderTabButtons()}
                  <div className="w-full md:w-auto p-3 flex items-center space-x-2 rounded-[5px] border-2 border-blue bg-white mt-2 md:mt-0">
                    <Search />
                    <input
                      placeholder="Search here"
                      className="outline-none w-full"
                      type="text"
                      value={searchQuery}
                      onChange={handleSearch}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    data-tour-id="new-request-button"
                    onClick={handleNewRequest}
                    className="w-full md:w-auto flex rounded-[5px] items-center space-x-4 py-2 px-4 text-white btnBg"
                  >
                    {activeTab === "request"
                      ? "New Request"
                      : "Generate TradeOS™ Quote"}
                  </button>
                </div>
              </div>
            )}

            {showForm ? (
              <AddressForm onBack={handleBack} />
            ) : showEstimateForm ? (
              <EstimateForm
                onBack={handleBackFromForm}
                estimate={selectedEstimate}
                fetchEstimates={fetchEstimates}
                isNewEstimate={isNewEstimate}
              />
            ) : (
              <div className="bg-white p-4 rounded-b-md" >
                {activeTab === "request" ? (
                  isLoadingRequests ? (
                    <div className="flex justify-center items-center py-8">
                      <Loader />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse table-fixed text-sm">
                        <thead>
                          <tr className="bg-gray-200">
                            <th className="p-2 text-center border-b">Name</th>
                            <th className="p-2 text-center border-b">
                              Creation Date
                            </th>
                            <th className="p-2 text-center border-b">
                              Inquire Number
                            </th>
                            <th className="p-2 text-center border-b">
                              Status
                            </th>
                            <th className="p-2 text-center border-b">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedData(filteredRequests).map(
                            (request, index) => (
                              <tr key={request.id} className="border-b-2">
                                <td className="p-2 text-center">
                                  {request.name}
                                </td>
                                <td className="p-2 text-center">
                                  {FormatDate(request.creationDate)}
                                </td>
                                <td className="p-2 text-center">
                                  {request.inquireNumber}
                                </td>
                                <td className="p-2 text-center capitalize">
                                  {normalizeRequestStatus(request.requestStatus)}
                                </td>
                                <td className="p-2 text-center">
                                  <Dropdown
                                    overlay={
                                      <Menu>
                                        {normalizeRequestStatus(request.requestStatus) === "unquoted" ? (
                                          <>
                                            <Menu.Item
                                              key="edit"
                                              onClick={() =>
                                                handleNewRequestForm(request)
                                              }
                                            >
                                              Edit
                                            </Menu.Item>
                                            <Menu.Item
                                              key="delete"
                                              onClick={() => handleDelete(request)}
                                            >
                                              Delete
                                            </Menu.Item>
                                          </>
                                        ) : (
                                          <Menu.Item key="locked" disabled>
                                            Locked (Quoted)
                                          </Menu.Item>
                                        )}
                                      </Menu>
                                    }
                                    visible={openDropdownIndex === index}
                                    trigger={["click"]}
                                    onVisibleChange={(visible) => {
                                      setOpenDropdownIndex(
                                        visible ? index : null
                                      );
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setOpenDropdownIndex(index);
                                    }}
                                  >
                                    <Space>
                                      <button className="border p-1 rounded-md border-[#053F92]">
                                        <PiDotsThreeVerticalBold size={20} />
                                      </button>
                                    </Space>
                                  </Dropdown>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : isLoadingEstimates ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader />
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse table-fixed text-sm">
                      <thead>
                        <tr className="bg-gray-200">
                          <th className="p-2 text-center border-b">
                            Proposal Id
                          </th>
                          <th className="p-2 text-center border-b">Date</th>
                          <th className="p-2 text-center border-b">Route</th>
                          <th className="p-2 text-center border-b">
                            Estimate Request Status
                          </th>
                          <th className="p-2 text-center border-b">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getPaginatedData(filteredEstimates).map(
                          (estimate, index) => (
                            <tr
                              key={estimate._id || index}
                              className="border-b-2"
                            >
                              <td className="p-2 text-center">
                                {estimate.estimateId}
                              </td>
                              <td className="p-2 text-center">
                                {FormatDate(estimate.createdAt)}
                              </td>
                              <td className="p-2 text-center">
                                {getRoute(estimate)}
                              </td>
                              <td className="p-2 text-center py-2 flex justify-center items-center gap-3">
                                <button
                                  data-tooltip-id="view"
                                  onClick={() => handleViewPdf(estimate)}
                                  className="py-1 px-3 rounded-md bg-[#A9A9A9] text-white disabled:bg-opacity-50"
                                  disabled={loadingStates[estimate.estimateId]?.view}
                                >
                                  {loadingStates[estimate.estimateId]?.view ? (
                                    <Loader2 className="animate-spin " />
                                  ) : (
                                    <FaRegEye className="text-2xl" />
                                  )}
                                </button>

                                {/* Accept Button - Hidden if Declined or already Accepted */}
                                {!estimate.estimationAccept && !estimate.isDeclined && (
                                  <button
                                    data-tooltip-id="Accept"
                                    onClick={() => handleAcceptEstimate(estimate)}
                                    className="py-1 px-3 rounded-md bg-[#7CE13E] text-white disabled:bg-opacity-50"
                                    disabled={loadingStates[estimate.estimateId]?.accept}
                                  >
                                    {loadingStates[estimate.estimateId]?.accept ? (
                                      <Loader2 className="animate-spin " />
                                    ) : (
                                      <FaCheckCircle className="text-2xl" />
                                    )}
                                  </button>
                                )}

                                <button
                                  data-tooltip-id="download"
                                  className="py-1 px-3 rounded-md bg-[#3861DC] text-white disabled:bg-opacity-50"
                                  onClick={() => handleDownloadPdf(estimate)}
                                  disabled={loadingStates[estimate.estimateId]?.download}
                                >
                                  {loadingStates[estimate.estimateId]?.download ? (
                                    <Loader2 className="animate-spin " />
                                  ) : (
                                    <FaDownload className="text-2xl" />
                                  )}
                                </button>

                                {/* Decline Button - Hidden if Declined or already Accepted */}
                                {!estimate.estimationAccept && !estimate.isDeclined && (
                                  <button
                                    data-tooltip-id="cancel"
                                    onClick={() =>
                                      handleDeclineEstimate(estimate.estimateId)
                                    }
                                    className="py-1 px-3 rounded-md bg-[#EE3025] text-white disabled:bg-opacity-50"
                                    disabled={loadingStates[estimate.estimateId]?.decline}
                                  >
                                    {loadingStates[estimate.estimateId]?.decline ? (
                                      <Loader2 />
                                    ) : (
                                      <MdOutlineCancel className="text-2xl" />
                                    )}
                                  </button>
                                )}

                                {/* Status Indicator for Declined items */}
                                {estimate.isDeclined && (
                                  <span className="text-red-500 font-bold ml-2 text-xs">Declined</span>
                                )}
                              </td>
                              <td className="p-2 text-center">
                                <Dropdown
                                  overlay={
                                    <Menu>
                                      <Menu.Item key="comment" onClick={() => handleCommentClick(estimate._id, estimate.estimateId)}>
                                        Comments
                                      </Menu.Item>
                                    </Menu>
                                  }
                                  trigger={["click"]}
                                >
                                  <Space>
                                    <button className="border relative p-1 rounded-md border-[#053F92]">
                                      <PiDotsThreeVerticalBold size={20} />
                                      {notifications?.some(
                                        (e) => e.quoteNumber === String(estimate.estimateId) && e.read === false
                                      ) && (
                                          <div className="w-2 h-2 absolute -top-[2px] right-0 bg-red-600 rounded-full"></div>
                                        )}
                                    </button>
                                  </Space>
                                </Dropdown>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
                <Tooltip id="view" place="top" content="View Estimate in PDF" />
                <Tooltip id="Accept" place="top" content="Accept Estimate" />
                <Tooltip
                  id="download"
                  place="top"
                  content="Download Estimate in PDF"
                />
                <Tooltip id="cancel" place="top" content="Cancel Estimate" />
                <div className="mt-4 flex justify-center">
                  <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={
                      activeTab === "request"
                        ? filteredRequests.length
                        : filteredEstimates.length
                    }
                    onChange={handlePageChange}
                    showSizeChanger
                    showQuickJumper
                    showTotal={(total, range) =>
                      `${range[0]}-${range[1]} of ${total} items`
                    }
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <Modal
          title={
            <div className="flex flex-col">
              <span>Comments</span>
              <span className="text-xs opacity-60">
                Proposal ID: {currentEstimateId?.estimateId ?? ""}
              </span>
            </div>
          }
          visible={showCommentModal}
          onCancel={() => setShowCommentModal(false)}
          footer={null}
          width={"70%"}
          styles={{ body: { padding: 0 } }}
        >
          {currentEstimateId && (
            <AskForEstimate
              user={user}
              id={currentEstimateId.id}
              estimateId={currentEstimateId.estimateId}
              proposalId={currentEstimateId.estimateId}
            />
          )}
        </Modal>

        <Modal
          title="Confirm Delete"
          visible={isDeleteModalVisible}
          onOk={confirmDelete}
          onCancel={cancelDelete}
          okText="Delete"
          cancelText="Cancel"
        >
          <p>Are you sure you want to delete this request?</p>
        </Modal>
      </div>
    </DashboardLayout>
  );
};

export default NewRequest;
